<template>
  <v-dialog v-model="show" max-width="560px" dark persistent>
    <v-card dark>
      <v-card-title class="text-h6">
        Copy to Stallholder
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-4">
        <p class="mb-4">
          <template v-if="mode === 'layout'">
            Copy this layout to another stallholder. Products are matched by
            Product Name on the destination stallholder.
          </template>
          <template v-else-if="productCount > 0 && categoryCount > 0">
            Copy
            <strong>{{ productCount }}</strong> product{{
              productCount === 1 ? "" : "s"
            }}
            and
            <strong>{{ categoryCount }}</strong> categor{{
              categoryCount === 1 ? "y" : "ies"
            }}
            that will also get copied
            to another stallholder.
          </template>
          <template v-else-if="productCount > 0">
            Copy
            <strong>{{ productCount }}</strong> product{{
              productCount === 1 ? "" : "s"
            }}
            to another stallholder.
          </template>
          <template v-else>
            Copy
            <strong>{{ categoryCount }}</strong> categor{{
              categoryCount === 1 ? "y" : "ies"
            }}
            to another stallholder.
          </template>
        </p>

        <v-select
          :items="availableYears"
          v-model="selectedYear"
          item-text="Year"
          return-object
          filled
          dense
          outlined
          hide-details
          label="Destination Year"
          class="mb-3"
          :disabled="copying"
          @change="handleYearChange"
        />

        <v-autocomplete
          :items="availableEvents"
          v-model="selectedEvent"
          item-text="EventName"
          return-object
          filled
          dense
          outlined
          hide-details
          label="Destination Event"
          class="mb-3"
          :loading="loadingEvents"
          :disabled="copying || loadingEvents || !selectedYear"
          @change="handleEventChange"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                <v-icon small color="red lighten-1">mdi-information</v-icon>
                No events found
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-autocomplete
          :items="availableStallholders"
          v-model="selectedStallholder"
          item-text="StallholderName"
          return-object
          filled
          dense
          outlined
          hide-details
          label="Destination Stallholder"
          class="mb-3"
          :loading="loadingStallholders"
          :disabled="copying || loadingStallholders || !selectedEvent"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                <v-icon small color="red lighten-1">mdi-information</v-icon>
                No stallholders found
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-alert v-if="sameDestination" type="warning" dense outlined class="mt-2">
          Destination must be different from the current event and stallholder.
        </v-alert>

        <v-alert v-if="errorMessage" type="error" dense outlined class="mt-2">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text :disabled="copying" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="copying"
          :disabled="!canSubmit"
          @click="submitCopy"
        >
          {{ submitLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { dashboardAPI } from "@/services";

export default {
  name: "CopyToStallholderDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    sourceEventId: {
      type: String,
      required: true,
    },
    sourceStallholderId: {
      type: String,
      default: "",
    },
    categoryIds: {
      type: Array,
      default: () => [],
    },
    productIds: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: "products",
    },
    submitLabel: {
      type: String,
      default: "Copy",
    },
  },

  data() {
    return {
      selectedYear: null,
      selectedEvent: null,
      selectedStallholder: null,
      availableYears: [],
      availableEvents: [],
      availableStallholders: [],
      loadingEvents: false,
      loadingStallholders: false,
      copying: false,
      errorMessage: "",
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    categoryCount() {
      return (this.categoryIds || []).length;
    },

    productCount() {
      return (this.productIds || []).length;
    },

    sameDestination() {
      if (!this.selectedEvent || !this.selectedStallholder) return false;
      return (
        this.selectedEvent.EventId === this.sourceEventId &&
        this.selectedStallholder.StallholderId === this.sourceStallholderId
      );
    },

    canSubmit() {
      const hasSelection =
        this.mode === "layout" ||
        this.categoryCount > 0 ||
        this.productCount > 0;
      return (
        !!this.selectedEvent &&
        !!this.selectedStallholder &&
        !this.sameDestination &&
        !this.copying &&
        hasSelection
      );
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.resetForm();
        this.populateYears();
      }
    },
  },

  methods: {
    resetForm() {
      this.selectedYear = null;
      this.selectedEvent = null;
      this.selectedStallholder = null;
      this.availableEvents = [];
      this.availableStallholders = [];
      this.errorMessage = "";
      this.copying = false;
    },

    populateYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // Always include the current calendar year so destinations like 2026 are available
      for (let year = currentYear; year >= 2020; year--) {
        years.push({ Year: year });
      }
      this.availableYears = years;
    },

    handleYearChange(year) {
      this.selectedEvent = null;
      this.selectedStallholder = null;
      this.availableEvents = [];
      this.availableStallholders = [];
      this.errorMessage = "";
      if (!year) return;

      this.loadingEvents = true;
      const api = dashboardAPI.getAdminGroupsEventsAPI();
      api
        .getEventsByYear(year.Year)
        .then((response) => {
          this.availableEvents = Array.isArray(response) ? response : [];
        })
        .catch((err) => {
          console.error("Error loading events:", err);
          this.availableEvents = [];
          this.errorMessage = "Failed to load events for that year.";
        })
        .finally(() => {
          this.loadingEvents = false;
        });
    },

    handleEventChange(event) {
      this.selectedStallholder = null;
      this.availableStallholders = [];
      this.errorMessage = "";
      if (!event) return;

      this.loadingStallholders = true;
      const api = dashboardAPI.getAdminGroupsEventsAPI();
      api
        .getEventMetadata(event.EventId)
        .then((response) => {
          this.availableStallholders = response?.Stallholders || [];
        })
        .catch((err) => {
          console.error("Error loading stallholders:", err);
          this.availableStallholders = [];
          this.errorMessage = "Failed to load stallholders for that event.";
        })
        .finally(() => {
          this.loadingStallholders = false;
        });
    },

    submitCopy() {
      if (!this.canSubmit) return;

      this.copying = true;
      this.errorMessage = "";

      const destination = {
        event: this.selectedEvent,
        stallholder: this.selectedStallholder,
      };

      if (this.mode === "layout") {
        this.$emit("copy", destination);
        return;
      }

      const payload = {
        event_items: [
          {
            Type: "product_copy",
            Action: "CopyBatch",
            Properties: {
              SourceEventId: this.sourceEventId,
              SourceStallholderId: this.sourceStallholderId,
              TargetEventId: this.selectedEvent.EventId,
              TargetStallholderId: this.selectedStallholder.StallholderId,
              CategoryIds: [...this.categoryIds],
              ProductIds: [...this.productIds],
            },
          },
        ],
      };

      this.$emit("copy", payload, destination);
    },

    setCopying(isCopying) {
      this.copying = isCopying;
    },

    setError(message) {
      this.errorMessage = message || "";
      this.copying = false;
    },

    close() {
      if (this.copying) return;
      this.show = false;
    },
  },
};
</script>
